import chartApi from "api/chart/chartApi";
import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import { useResize } from "components/Hooks/useResize";
import { INTERVAL_OPTIONS, PERIOD_OPTIONS } from "helpers/constans";
import {
  CandlestickData,
  CandlestickSeries,
  createChart,
  CrosshairMode,
  HistogramData,
  HistogramSeries,
  IChartApi,
  ISeriesApi,
  Time,
} from "lightweight-charts";
import moment from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Col, Row, Spinner } from "reactstrap";

interface IParams {
  address: string,
  timePeriod: string,
  timeFrame: string,
  fromDate: string,
  toDate: string,
  currency: string,
  limit: string,
  isMore: boolean
};

const SolanaChart = ({
  pairAddress
}: {
  pairAddress: string,
}) => {
  const { t, i18n } = useTranslation();

  const { layoutModeType } = useSelector((state: any) => state.Layout);

  const _isDark: boolean = (layoutModeType === "dark");

  const CLIENT_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getDateRangeByPeriod = (_period: string = '') => {

    const validRanges = ['1', '3', '5', '7', '30'];
    if (!validRanges.includes(_period)) {
      throw new Error('Invalid rangeType. Must be one of: ' + validRanges.join(', '));
    }

    const now = moment().utc();

    const timezoneOffsetHours = moment().utcOffset() / 60;

    const rangeMap: any = {
      '1': { unit: 'days', value: 0 },
      '3': { unit: 'days', value: 2 },
      '5': { unit: 'days', value: 4 },
      '7': { unit: 'days', value: 6 },
      '30': { unit: 'month', value: 1 },
    };

    const { unit, value } = rangeMap[_period];

    const range = {
      fromDate: moment(now).startOf('day').subtract(value, unit).subtract(timezoneOffsetHours, 'hours').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      endDate: moment(now).add(30, 'minutes').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
    };

    return range;
  };

  const PERIOD_OPTIONS_LANG = PERIOD_OPTIONS?.map((item: any) => ({
    value: item?.value,
    label: t(item?.label),
  }));

  const INTERVAL_OPTIONS_LANG = INTERVAL_OPTIONS?.map((item: any) => ({
    value: item?.value,
    label: t(item?.label),
  }));

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);

  const [lastedTimeCall, setLastedTimeCall] = useState<any>('');

  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const { height } = useResize();

  const [statusChart, setStatusChart] = useState<any>();

  const TIME_DEFAULT = getDateRangeByPeriod(PERIOD_OPTIONS_LANG[0]?.value);
  const [params, setParams] = useState<IParams>({
    address: '',
    timePeriod: PERIOD_OPTIONS_LANG[0]?.value,
    timeFrame: INTERVAL_OPTIONS_LANG[0]?.value,
    fromDate: TIME_DEFAULT?.fromDate,
    toDate: TIME_DEFAULT?.endDate,
    currency: "usd",
    limit: "300",
    isMore: false,
  });

  const removeDuplicates = (arr: any, key: string) => {
    return Array.from(
      new Map(arr.map((item: any) => [item[key], item])).values()
    );
  };

  const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout | null = null;
    return (...args: Parameters<T>) => {
      if (timeout) {
        clearTimeout(timeout);
      };
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const ensureSortedByTime = <T extends { time?: Time | any }>(data: T[]): T[] => {
    try {
      const sorted = [...data].sort((a, b) => {
        const timeA = typeof a?.time === "string" ? Number(a?.time) : a?.time;
        const timeB = typeof b?.time === "string" ? Number(b?.time) : b?.time;
        return (timeA as number) - (timeB as number);
      });
      if (JSON.stringify(data) !== JSON.stringify(sorted)) {
        console.warn("Data was not sorted by time. Fixed automatically.");
      }
      return sorted;
    } catch (error) {
      console.error(error);
      return data;
    }
  };

  const convertDataForChart = (data: any) => {
    return data.map((item: any) => {
      if (!item?.timestamp) {
        console.warn("Invalid timestamp:", item);
        return null;
      }
      const timestamp = moment.utc(item?.timestamp).tz(CLIENT_TIME_ZONE);
      return {
        time: timestamp.unix(),
        open: item?.open,
        high: item?.high,
        low: item?.low,
        close: item?.close,
        volume: item?.volume,
      };
    }).filter((item: any) => item !== null);
  };

  const convertDataForVolume = (data: any) => {
    return data.map((item: any) => {
      const timestamp = moment.utc(item?.timestamp).tz(CLIENT_TIME_ZONE);
      const isBullish = item?.close > item?.open;
      const isBearish = item?.close < item?.open;
      const volumeColor = isBullish ? "#26a69a80" : (isBearish ? "#ef535080" : "#80808080"); //
      return {
        time: timestamp.unix(),
        value: item?.volume,
        color: volumeColor,
      };
    }).filter((item: any) => !!item?.time);
  };

  const calculateChartStats = (data: any) => {
    if (data.length === 0 || data?.[0] === undefined) {
      return ({ open: 0, close: 0, high: 0, low: 0, volume: 0, change: 0, color: "#80808080" });
    };
    const latestCandle = data?.[0];
    const open = latestCandle?.open;
    const close = latestCandle?.close;
    const high = latestCandle?.high;
    const low = latestCandle?.low;
    const volume = latestCandle?.volume;
    const change = ((close - open) / open) * 100;
    return {
      open, close, high, low, volume, change, color: (change && change < 0) ? "#ef535080" : "#26a69a80",
    };
  };

  const updateChartStats = (data: any) => {
    setStatusChart(data);
  };


  // =========Begin::Code Here==========
  useEffect(() => {

    if (!chartContainerRef.current) {
      return;
    };

    moment.locale(i18n.language);

    chartRef.current = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 500,
      layout: {
        background: { color: _isDark ? "#1E252B" : "#FFFFFF" },
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
        fontSize: 12,
      },
      grid: {
        vertLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
        horzLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          visible: true,
          labelVisible: false, // 👈 ẩn label dọc (label thời gian)
        },
        horzLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          width: 1,
          style: 1,
          labelBackgroundColor: _isDark ? "#2A2E39" : "#E0E3EB",
        },
      },
      rightPriceScale: {
        borderColor: _isDark ? "#2A2E39" : "#E0E3EB",
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
      },
      timeScale: {
        borderColor: _isDark ? "#2A2E39" : "#E0E3EB",
        timeVisible: true,
        secondsVisible: true,
        tickMarkFormatter: (time: number, tickIndex: number) => {
          const date = moment.unix(time).tz(CLIENT_TIME_ZONE);
          if (["1s", "10s", "30s", "1min", "5min", "10min"].includes(params?.timeFrame)) {
            return moment(date).format("HH:mm:ss");
          } else if (params?.timeFrame === "1h" || params?.timeFrame === "4h" || params?.timeFrame === "12h") {
            return date.format("MM/DD HH:mm");
          }
          return date.format("MM/DD HH:mm");
        },
      },
    });

    candleSeriesRef.current = chartRef.current.addSeries(CandlestickSeries, {
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderUpColor: "#26a69a",
      borderDownColor: "#ef5350",
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
      priceFormat: {
        precision: 6,
        minMove: 0.000001,
      },
    });

    volumeSeriesRef.current = chartRef.current.addSeries(HistogramSeries, {
      color: _isDark ? "#3B4A5A" : "#D3D3D3",
      priceFormat: { type: "volume" },
      priceScaleId: "volume-scale",
    });

    chartRef.current?.priceScale("volume-scale").applyOptions({
      scaleMargins: { top: 0.75, bottom: 0 },
      mode: 0,
      autoScale: true,
    });

    const tooltip = document.createElement("div");
    tooltip.setAttribute("id", "time-tooltip");
    tooltip.style.position = "absolute";
    tooltip.style.bottom = "16px";
    tooltip.style.transform = "translateX(-50%)";

    tooltip.style.color = _isDark ? "#D1D4DC" : "#2A2E39";
    tooltip.style.borderRadius = "6px";
    tooltip.style.boxShadow = "var(--shadow-sm)";
    tooltip.style.padding = "2px 8px";
    tooltip.style.fontSize = "13px";
    tooltip.style.pointerEvents = "none";
    tooltip.style.zIndex = "1000";
    chartContainerRef.current.appendChild(tooltip);

    const xAxisLabel = document.createElement("div");
    const offset = moment().tz(CLIENT_TIME_ZONE).format("Z");
    xAxisLabel.textContent = `UTC ${offset}`;
    xAxisLabel.style.position = "absolute";
    xAxisLabel.style.bottom = "20px";
    xAxisLabel.style.right = "4px";
    xAxisLabel.style.fontSize = "12px";
    xAxisLabel.style.fontWeight = "500";
    xAxisLabel.style.color = "#000";
    xAxisLabel.style.pointerEvents = "none";
    xAxisLabel.style.zIndex = "999";
    chartContainerRef.current.appendChild(xAxisLabel);

    chartRef.current?.subscribeCrosshairMove((param) => {
      if (!param.time || !param.seriesData) return;
      const tooltipEl = document.getElementById("time-tooltip");
      if (!tooltipEl) return;

      if (!param.time || !param.point) {
        tooltipEl.style.display = "none";
        return;
      }
      tooltip.style.background = _isDark ? "#2A2E39" : "#E0E3EB";
      tooltip.style.border = "1px solid #ccc";
      const timestamp = Number(param.time);
      const localTimeStr = moment
        .unix(timestamp)
        .tz(moment.tz.guess())
        .format(`MM/DD HH:mm`);

      tooltipEl.textContent = localTimeStr;
      tooltipEl.style.display = "block";

      tooltipEl.style.transform = `translateX(${param.point.x}px)`;

      const candleData = param.seriesData.get(candleSeriesRef.current!);
      const volumeData: any = param.seriesData.get(volumeSeriesRef.current!);
      const newParam: any = {
        open: 0,
        high: 0,
        low: 0,
        close: 0,
        volume: 0,
      };
      if (candleData) {
        const { open, high, low, close } = candleData as any;

        newParam.open = open;
        newParam.high = high;
        newParam.low = low;
        newParam.close = close;
      }

      if (volumeData) {
        newParam.volume = volumeData?.value;
      }
      const stats = calculateChartStats([newParam]);

      updateChartStats(stats);
    });

    return () => {
      chartRef.current?.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.applyOptions({
      layout: {
        background: { color: _isDark ? "#1E252B" : "#FFFFFF" },
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
      },
      grid: {
        vertLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
        horzLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          labelBackgroundColor: _isDark ? "#2A2E39" : "#E0E3EB",
        },
        horzLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          labelBackgroundColor: _isDark ? "#2A2E39" : "#E0E3EB",
        },
      },
      rightPriceScale: {
        borderColor: _isDark ? "#2A2E39" : "#E0E3EB",
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
      },
    });

    if (volumeSeriesRef.current) {
      volumeSeriesRef.current.applyOptions({
        color: _isDark ? "#3B4A5A" : "#D3D3D3",
      });
    }
  }, [_isDark]);


  const getOHLCVData = async (
    _params: any
  ) => {
    try {
      setIsLoadingMore(true);
      const response: any = await chartApi.getPairOhlcv(_params);
      setIsLoadingMore(false);

      const newData = response?.data?.result;
      const timeEndNext: string = (!response?.data?.cursor || !newData?.length) ? '' : (newData?.[newData?.length - 1]?.timestamp || '');
      setLastedTimeCall(timeEndNext);


      const sortedChartData: any = ensureSortedByTime(convertDataForChart(newData)) || [];
      const sortedVolumeData: any = ensureSortedByTime(convertDataForVolume(newData)) || [];

      if (!!_params?.isMore) {

        if (!newData?.length) { return; };

        const existingChartData = (candleSeriesRef.current?.data() as CandlestickData[]) || [];
        const existingVolumeData = (volumeSeriesRef.current?.data() as HistogramData[]) || [];

        const mergedChartData: any = removeDuplicates(sortedChartData.concat(existingChartData), 'time');
        const mergedVolumeData: any = removeDuplicates(sortedVolumeData.concat(existingVolumeData), 'time');

        candleSeriesRef.current?.setData(mergedChartData);
        volumeSeriesRef.current?.setData(mergedVolumeData);
      } else {
        const chartStats = calculateChartStats(newData);
        updateChartStats(chartStats);
        candleSeriesRef.current?.setData(sortedChartData);
        volumeSeriesRef.current?.setData(sortedVolumeData);
        // candleSeriesRef.current?.moveToPane(0);
      }

    } catch (error) {
      setIsLoadingMore(false);
      console.error(error);
      toast(t(`${error}`), CONFIG_OPTION_TOAST_ERROR);
    }
  };

  const handleChangeInterval = (_interval: any) => {
    if (isLoadingMore) {
      return;
    }
    setParams((_prev) => {
      const _TIME_DEFAULT = getDateRangeByPeriod(_prev?.timePeriod);
      return ({
        ..._prev,
        timeFrame: _interval?.value,
        fromDate: _TIME_DEFAULT?.fromDate,
        toDate: _TIME_DEFAULT?.endDate,
        isMore: false,
      })
    });
  };

  const handleChangePeriod = (_period: any) => {
    if (isLoadingMore) {
      return;
    }
    const _TIME_DEFAULT = getDateRangeByPeriod(_period?.value);
    setParams((_prev) => ({
      ..._prev,
      timePeriod: _period?.value,
      fromDate: _TIME_DEFAULT?.fromDate,
      toDate: _TIME_DEFAULT?.endDate,
      isMore: false,
    }));
  };

  useEffect(() => {
    if (!!pairAddress) {
      setParams((_prev) => ({ ..._prev, address: pairAddress, isMore: false }));
    };
  }, [pairAddress]);

  useEffect(() => {
    if (!!params?.address) {
      getOHLCVData(params);
    };
  }, [JSON.stringify(params)]);


  useEffect(() => {

    const handleVisibleTimeRangeChange = () => {
      try {

        if (isLoadingMore || !lastedTimeCall) return;

        const timeScale = chartRef.current?.timeScale();
        const visibleRange = timeScale?.getVisibleLogicalRange();

        if (Number(visibleRange?.from || 0) <= 200) {
          const toDate = moment.utc(lastedTimeCall).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
          setParams((_prev) => ({
            ..._prev,
            toDate: toDate,
            isMore: true
          }));
        }
      } catch (error) {
        console.error(error);
      }

    };

    const debouncedHandleVisibleTimeRangeChange = debounce(handleVisibleTimeRangeChange, 800);

    chartRef.current?.timeScale().subscribeVisibleTimeRangeChange(debouncedHandleVisibleTimeRangeChange);

    return () => {
      chartRef.current?.timeScale().unsubscribeVisibleTimeRangeChange(debouncedHandleVisibleTimeRangeChange);
    };

  }, [isLoadingMore, lastedTimeCall]);


  useEffect(() => {
    if (!chartContainerRef.current || !chartRef.current) return;

    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      const { width, height: heightContent } = entries[0].contentRect;
      let newHeight = heightContent;
      if (height) {
        newHeight = height;
      }
      try {
        chartRef.current?.applyOptions({ width, height: newHeight });
      } catch (error) {
        console.error("Error updating chart size:", error);
      }
    }, 100);

    // Khởi tạo ResizeObserver
    resizeObserver.current = new ResizeObserver(handleResize);
    resizeObserver.current.observe(chartContainerRef.current);

    return () => {
      resizeObserver.current?.disconnect();
    };

  }, [chartRef.current, height]);

  useEffect(() => {
    if (!chartRef.current || !chartContainerRef) return;
    moment.locale(i18n.language);
    chartRef.current.applyOptions({
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          labelBackgroundColor: _isDark ? "#2A2E39" : "#F6F6F6",
        },
        horzLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          labelBackgroundColor: _isDark ? "#2A2E39" : "#F6F6F6",
        },
      },
      rightPriceScale: {
        borderColor: _isDark ? "#2A2E39" : "#F6F6F6",
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
      },
    });
  }, [i18n.language, height]);

  return (
    <div>
      <div className="px-2 mb-3 mt-2">
        <Row>
          <Col sm={5}>
            <div className="d-flex justify-content-start">
              <div className="flex items-center rounded-lg p-1 space-x-1" style={{ width: 'fit-content', minWidth: '270px' }}>
                <button
                  className="px-2 px-md-2  py-1 text-gray-700 rounded-md focus:outline-none transition duration-200 ease-in-out"
                  disabled
                >
                  {t("Period")}
                </button>
                <span className="text-gray-400 ms-0 me-0">|</span>
                {PERIOD_OPTIONS_LANG.map((_period) => (
                  <button
                    key={_period?.value}
                    onClick={() => handleChangePeriod(_period)}
                    className={`px-2 px-md-2 py-1 text-gray-700 rounded-md focus:outline-none transition duration-200 ease-in-out ${(params?.timePeriod === _period?.value) ? 'bg-gray-400 text-white' : ' hover:bg-gray-200'
                      }`}
                  >
                    {_period?.label}
                  </button>
                ))}
              </div>
            </div>
          </Col>

          <Col sm={7}>
            <div className="d-flex justify-content-start justify-content-sm-end mt-2 mt-sm-0">
              <div className="flex items-center rounded-lg p-1 space-x-1" style={{ width: 'fit-content', minWidth: '330px' }}>
                <button
                  className="px-2 px-md-2  py-1 text-gray-700 rounded-md focus:outline-none transition duration-200 ease-in-out"
                  disabled
                >
                  {t("Interval")}
                </button>
                <span className="text-gray-400 ms-0 me-0">|</span>
                {INTERVAL_OPTIONS_LANG.map((_interval) => (
                  <button
                    key={_interval?.value}
                    onClick={() => handleChangeInterval(_interval)}
                    className={`px-2 px-md-2 py-1 text-gray-700 rounded-md focus:outline-none transition duration-200 ease-in-out ${(params?.timeFrame === _interval?.value) ? 'bg-gray-400 text-white' : ' hover:bg-gray-200'
                      }`}
                  >
                    {_interval?.label}
                  </button>
                ))}
              </div>
            </div>
          </Col>

        </Row>
      </div>
      <div className="position-relative">
        <Row className="w-full align-items-center ps-1 position-absolute"
          style={{
            zIndex: 2,
            top: '2px',
            left: '10px',
            pointerEvents: 'none'
          }}
        >
          <Col
            sm={12}
            md={12}
            xl={12}
            xxl={12}
            className="flex flex-wrap gap-2 md-mt-3"
          >
            <div className="point-o">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="18" height="18" fill="none"><circle fill="currentColor" cx="9" cy="9" r="4"></circle></svg>
            </div>
            <div className="flex gap-1">
              <span
                className="text-gray-400 fs-13"
              >
                {t("Label_Open")}:{" "}
              </span>

              <span
                style={{
                  color:
                    statusChart?.change >= 0
                      ? "#26a69a"
                      : "#ef5350",
                }}
              >
                {statusChart?.open?.toFixed(6) || "-"}
              </span>
            </div>

            <div className="flex gap-1">
              <span
                className="text-gray-400 fs-13"
              >
                {t("Label_High")}:{" "}
              </span>

              <span
                style={{
                  color:
                    statusChart?.change >= 0
                      ? "#26a69a"
                      : "#ef5350",
                }}
              >
                {statusChart?.high?.toFixed(6) || "-"}
              </span>
            </div>
            <div className="flex gap-1">
              <span
                className="text-gray-400 fs-13"
              >
                {t("Label_Low")}:{" "}
              </span>

              <span
                style={{
                  color:
                    statusChart?.change >= 0
                      ? "#26a69a"
                      : "#ef5350",
                }}
              >
                {statusChart?.low?.toFixed(6) || "-"}
              </span>
            </div>
            <div className="flex gap-1">
              <span
                className="text-gray-400 fs-13"
              >
                {t("Label_Close")}:{" "}
              </span>

              <span
                style={{
                  color:
                    statusChart?.change >= 0
                      ? "#26a69a"
                      : "#ef5350",
                }}
              >
                {statusChart?.close?.toFixed(6) || "-"}
              </span>
            </div>

            <div className="flex gap-1">
              <span
                className="text-gray-400 fs-13"
              >
                {t("Label_Volume")}:{" "}
              </span>

              <span
                style={{
                  color:
                    statusChart?.change >= 0
                      ? "#26a69a"
                      : "#ef5350",
                }}
              >
                {statusChart?.volume?.toFixed(2) || "-"}
              </span>
            </div>
            <div className="flex gap-1">
              <span
                className="text-gray-400 fs-13"
              >
                {t("Label_Change")}:{" "}
              </span>
              <span
                style={{
                  color:
                    statusChart?.change >= 0
                      ? "#26a69a"
                      : "#ef5350",
                }}
              >
                {statusChart?.change >= 0 ? "+" : ""}
                {statusChart?.change?.toFixed(2) || 0}%
              </span>
            </div>
          </Col>
        </Row>
        <div style={{ position: "relative", display: "flex" }}>
          <div style={{ flex: 1 }}>
            <div
              ref={chartContainerRef}
              style={{
                width: "100%",
                height: height ? `${height}px` : "50dvh",
                minHeight: height ? `${height}px` : "500px",
              }}
            />
          </div>
          {isLoadingMore && (
            <div
              className="position-absolute"
              style={{
                zIndex: 9,
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -100%)",
              }}
            >
              <Spinner
                size="sm"
                style={{ width: "50px", height: "50px", color: "#26a69a" }}
              ></Spinner>
            </div>
          )}
        </div>
        <div
          id="resize-bar"
          className="resizable-area cursor-ns-resize flex items-center justify-center "
        >
          <div className="icon-left">
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" focusable="false" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M34.9 289.5l-22.2-22.2c-9.4-9.4-9.4-24.6 0-33.9L207 39c9.4-9.4 24.6-9.4 33.9 0l194.3 194.3c9.4 9.4 9.4 24.6 0 33.9L413 289.4c-9.5 9.5-25 9.3-34.3-.4L264 168.6V456c0 13.3-10.7 24-24 24h-32c-13.3 0-24-10.7-24-24V168.6L69.2 289.1c-9.3 9.8-24.8 10-34.3.4z"></path></svg>
          </div>
          <div className="line"></div>
          <div className="icon-right">
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" focusable="false" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M413.1 222.5l22.2 22.2c9.4 9.4 9.4 24.6 0 33.9L241 473c-9.4 9.4-24.6 9.4-33.9 0L12.7 278.6c-9.4-9.4-9.4-24.6 0-33.9l22.2-22.2c9.5-9.5 25-9.3 34.3.4L184 343.4V56c0-13.3 10.7-24 24-24h32c13.3 0 24 10.7 24 24v287.4l114.8-120.5c9.3-9.8 24.8-10 34.3-.4z"></path></svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SolanaChart);
